/**
 * Ton auf dem iPhone: warum ein Voiceover schweigt, das da ist.
 *
 * Safari auf iOS lässt ein Medienelement nur klingen, wenn sein allererstes
 * `play()` aus einer Berührung kam. Danach ist genau *dieses* Element frei,
 * für immer; ein anderes, das später entsteht, ist es nicht.
 *
 * Die Vorschau legte ihre Tonelemente aber erst an, wenn ein Clip hörbar
 * wurde -- also mitten in der Wiedergabe, weit weg von dem Tippen auf *Play*.
 * iOS wies das `play()` ab, der Fangarm schluckte den Fehler, und Sascha hörte
 * nichts oder Bruchstücke. („Leider spielt mein Audio-Voiceover nicht ab bei
 * iOS. Es hakt ziemlich.“)
 *
 * Deshalb diese kleine Stelle: die Vorschau meldet hier an, wie ihre Elemente
 * freizuschalten sind, und alles, was Wiedergabe *aus einer Berührung heraus*
 * startet, ruft `tonFreischalten()` im selben Zug auf -- bevor `setPlaying`
 * irgendetwas Asynchrones anfängt.
 */

import { pruefton, wecke, zustand } from './tonkette';

type Freischalter = () => void;

let angemeldet: Freischalter | null = null;

/** Die Vorschau meldet ihren Freischalter an und beim Aufräumen wieder ab. */
export function meldeFreischalter(fn: Freischalter): () => void {
  angemeldet = fn;
  return () => {
    if (angemeldet === fn) angemeldet = null;
  };
}

/**
 * Aus einer Berührung heraus aufrufen, unmittelbar bevor die Wiedergabe
 * startet. Ohne angemeldete Vorschau tut es nichts -- das ist kein Fehler,
 * sondern der Fall „es gibt gerade keine Vorschau“.
 */
/** Für die Prüfung und die Diagnose sichtbar: wie es der Tonkette geht. */
declare global {
  interface Window {
    __ton?: () => string;
    /** Ein Prüfton durch die Kette, für den Pegelmesser (28. Oktober). */
    __pruefton?: (links: number, rechts: number, sekunden: number) => boolean;
    __freischalter?: () => string;
  }
}
if (typeof window !== 'undefined') {
  window.__ton = zustand;
  window.__pruefton = pruefton;
}

/**
 * **Was das letzte Tippen angestossen hat** (7.230). Fiel die Prüfung aus,
 * stand nur da, *dass* nichts angestossen war -- nicht, ob gar kein
 * Freischalter angemeldet war oder ob er die Flächen übersprungen hat, weil
 * sie noch keine Datei hatten. Die Vorschau schreibt je Fläche eine Zeile.
 */
let aufrufe = 0;
let letzter: { angemeldet: boolean; flaechen: string[] } | null = null;
export function vermerkeFreischalten(zeile: string): void {
  letzter?.flaechen.push(zeile);
}
if (typeof window !== 'undefined') window.__freischalter = () => JSON.stringify({ aufrufe, letzter });

export function tonFreischalten(): void {
  // Zuerst die Tonkette: sie ist der eine Weg, den iOS erlauben muss, und
  // `resume()` will genauso aus einer Berührung kommen wie ein `play()`
  // (7.39). Die Elemente danach sind nur noch die Rückfallebene.
  wecke();
  aufrufe += 1;
  letzter = { angemeldet: angemeldet !== null, flaechen: [] };
  angemeldet?.();
}
